// src/screens/FormCreatorScreen.js

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  View, 
  ScrollView, 
  Modal, 
  TouchableOpacity, 
  Alert,
  findNodeHandle,
  UIManager,
  Animated,
  Easing
} from 'react-native';
import { 
  Appbar, 
  Card, 
  Title, 
  Paragraph, 
  Button, 
  TextInput,
  Text,
  Chip,
  Badge,
  IconButton,
  Divider,
  ActivityIndicator,
  ProgressBar
} from 'react-native-paper';
import AsyncStorage from '@react-native-async-storage/async-storage';

import api from '../lib/api';
import { transformFormToApi } from '../utils/transformers';
import StorageManager from '../utils/StorageManager';

import { MaterialCommunityIcons } from '@expo/vector-icons';  

// Componente para destacar texto pesquisado
const HighlightedText = ({ text, terms, style }) => {
  if (!terms || terms.length === 0 || !text) return <Text style={style}>{text}</Text>;

  try {
    const escapedTerms = terms.map(term =>
      term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    );

    const regex = new RegExp(`(${escapedTerms.join('|')})`, 'gi');
    const parts = text.split(regex);

    return (
      <Text style={style}>
        {parts.map((part, i) => {
          const isMatch = terms.some(term =>
            part.toLowerCase() === term.toLowerCase() ||
            part.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '') ===
            term.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
          );

          return (
            <Text
              key={i}
              style={isMatch ? { backgroundColor: '#ffff00', fontWeight: 'bold' } : {}}
            >
              {part}
            </Text>
          );
        })}
      </Text>
    );
  } catch (error) {
    console.error('Erro no HighlightedText:', error);
    return <Text style={style}>{text}</Text>;
  }
};

// Componente de animação de carregamento
const SearchingIndicator = ({ isSearching }) => {
  const spinValue = useRef(new Animated.Value(0)).current;
  
  useEffect(() => {
    if (isSearching) {
      Animated.loop(
        Animated.timing(spinValue, {
          toValue: 1,
          duration: 1500,
          easing: Easing.linear,
          useNativeDriver: true
        })
      ).start();
    } else {
      spinValue.setValue(0);
    }
  }, [isSearching, spinValue]);
  
  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg']
  });
  
  if (!isSearching) return null;
  
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', marginLeft: 4 }}>
      <Animated.Text 
        style={{ 
          transform: [{ rotate: spin }],
          fontSize: 14,
          marginRight: 4
        }}
      >
        ⏳
      </Animated.Text>
      <Text style={{ fontSize: 12, color: '#666', fontStyle: 'italic' }}>
        Pesquisando...
      </Text>
    </View>
  );
};

import { AVAQualisTheme } from '../theme/theme';
import { styles } from './FormCreatorScreen.css';
import { FORMS_DATA } from '../data/FormsData';
import { 
  ESTRUTURA_ORGANIZACIONAL, 
  searchUnidades, 
  formatUnidadeDisplay,
  getUnidadeHierarchy,
  AVALIADORES 
} from '../data/EstruturaOrganizacional';

// Base de dados de todos os requisitos disponíveis
const ALL_REQUIREMENTS = (() => {
  const requirements = [];
  
  // Extrair todos os requisitos de todos os formulários
  Object.values(FORMS_DATA).forEach(formData => {
    if (formData.sections) {
      formData.sections.forEach(section => {
        section.questions.forEach(question => {
          requirements.push({
            ...question,
            sectionInfo: {
              tipo: section.tipo,
              categoria: section.categoria,
              subcategoria: section.subcategoria
            }
          });
        });
      });
    }
  });
  
  // Remover duplicatas baseado no código
  const uniqueRequirements = requirements.filter((req, index, self) => 
    index === self.findIndex(r => r.codigo === req.codigo)
  );
  
  // Ordenar por código
  return uniqueRequirements.sort((a, b) => {
    const codeA = a.codigo.split('.').map(n => parseInt(n) || 0);
    const codeB = b.codigo.split('.').map(n => parseInt(n) || 0);
    
    for (let i = 0; i < Math.max(codeA.length, codeB.length); i++) {
      const numA = codeA[i] || 0;
      const numB = codeB[i] || 0;
      if (numA !== numB) return numA - numB;
    }
    return 0;
  });
})();

// Componente personalizado - TextInput com label cinza
const GrayLabelTextInput = ({ label, ...props }) => {
  return (
    <TextInput
      {...props}
      label={label}
      theme={{
        colors: {
          onSurfaceVariant: '#9E9E9E',
          outline: '#9E9E9E',
        }
      }}
    />
  );
};

// Função auxiliar para obter complexidade do formulário
const getFormComplexity = (questionCount) => {
  if (questionCount <= 8) return { label: 'Rápido', color: AVAQualisTheme.colors.success, icon: '⚡' };
  if (questionCount <= 15) return { label: 'Médio', color: AVAQualisTheme.colors.warning, icon: '⏱️' };
  return { label: 'Detalhado', color: AVAQualisTheme.colors.error, icon: '📋' };
};

// Função auxiliar para contar requisitos essenciais
const countEssentialRequirements = (form) => {
  if (!form.requirements) return 0;
  return form.requirements.filter(req => req.isEssential).length;
};

const CustomDialog = ({ visible, onDismiss, title, message, actions, children }) => {
  return (
    <Modal
      visible={visible}
      transparent={true}
      animationType="fade"
      onRequestClose={onDismiss}
    >
      <TouchableOpacity 
        style={styles.modalOverlay} 
        activeOpacity={1} 
        onPress={onDismiss}
      >
        <TouchableOpacity 
          style={styles.dialogContainer} 
          activeOpacity={1}
          onPress={() => {}}
        >
          <View style={styles.dialogHeader}>
            <Text style={styles.dialogTitle}>{title}</Text>
          </View>
          
          <View style={styles.dialogContent}>
            {children || <Text style={styles.dialogMessage}>{message}</Text>}
          </View>
          
          <View style={styles.dialogActions}>
            {actions.map((action, index) => (
              <TouchableOpacity
                key={index}
                style={[
                  styles.dialogButton,
                  index === actions.length - 1 ? styles.dialogButtonPrimary : styles.dialogButtonSecondary
                ]}
                onPress={() => {
                  action.action();
                  onDismiss();
                }}
              >
                <Text style={[
                  styles.dialogButtonText,
                  index === actions.length - 1 ? styles.dialogButtonTextPrimary : styles.dialogButtonTextSecondary
                ]}>
                  {action.text}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

// COMPONENTE - Exibição de unidades organizacionais no formato do FormListScreen
const UnidadesOrgDisplay = ({ unidades }) => {
  if (!unidades || unidades.length === 0) return null;
  
  return (
    <View style={styles.unidadesExpandable}>
      <Text style={styles.unidadesLabel}>
        🏢 Unidades Organizacionais ({unidades.length}):
      </Text>
      <View style={styles.unidadesInlineContainer}>
        {unidades.map((unidade, index) => (
          <View key={unidade.id} style={styles.unidadeCardInline}>
            <View style={styles.unidadeCardInlineHeader}>
              <Text style={styles.unidadeCardInlineIndex}>{index + 1}</Text>
              <Text style={styles.unidadeCardInlineCode}>{unidade.codigo}</Text>
            </View>
            <Text style={styles.unidadeCardInlineName}>{unidade.nome}</Text>
            <Text style={styles.unidadeCardInlineContact}>
              📞 {unidade.telefone} • 📧 {unidade.email}
            </Text>
            <Text style={styles.unidadeCardInlineHierarchy}>
              🏗️ {getUnidadeHierarchy(unidade.id)}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
};

// COMPONENTE - Indicador de carregamento para sincronização
const SyncLoadingIndicator = ({ syncingForms }) => {
  if (!syncingForms) return null;
  
  return (
    <Card style={styles.syncingCard}>
      <Card.Content>
        <View style={styles.syncingContent}>
          <ActivityIndicator size="small" color={AVAQualisTheme.colors.primary} />
          <Text style={styles.syncingText}>Sincronizando com o servidor...</Text>
        </View>
      </Card.Content>
    </Card>
  );
};

// Função auxiliar para sanitizar dados antes de salvar
const sanitizeFormData = (data) => {
  try {
    // Converter para JSON e voltar para garantir que é serializável
    const jsonString = JSON.stringify(data);
    return JSON.parse(jsonString);
  } catch (error) {
    console.error('❌ Erro ao sanitizar dados:', error);
    throw new Error('Dados do formulário contêm caracteres inválidos');
  }
};

export default function FormCreatorScreen({ onBack }) {
  // Estados principais
  const [formTitle, setFormTitle] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedRequirements, setSelectedRequirements] = useState([]);
  const [filteredRequirements, setFilteredRequirements] = useState(ALL_REQUIREMENTS);
  const [expandedSections, setExpandedSections] = useState({});
  const [customForms, setCustomForms] = useState([]);
  
  // Estados para pesquisa
  const [searchTerms, setSearchTerms] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResultMessage, setSearchResultMessage] = useState('');
  const searchTimeoutRef = useRef(null);
  
  // Estados - Metadados do formulário
  const [avaliador1, setAvaliador1] = useState('');
  const [avaliador2, setAvaliador2] = useState('');
  const [dataAvaliacao, setDataAvaliacao] = useState('');
  const [horarioAvaliacao, setHorarioAvaliacao] = useState('');
  const [contatoAvaliacao, setContatoAvaliacao] = useState('');
  const [localAvaliacao, setLocalAvaliacao] = useState('');
  // Array de unidades selecionadas
  const [selectedUnidades, setSelectedUnidades] = useState([]);
  const [unidadeSearchText, setUnidadeSearchText] = useState('');
  const [filteredUnidades, setFilteredUnidades] = useState([]);
  const [showUnidadeDropdown, setShowUnidadeDropdown] = useState(false);
// Estados para Processo Avaliado
const [selectedProcessos, setSelectedProcessos] = useState([]); // MUDANÇA: Array de processos
const [selectedProcesso, setSelectedProcesso] = useState(null); // Manter para compatibilidade temporária
const [processoSearchText, setProcessoSearchText] = useState('');
const [filteredProcessos, setFilteredProcessos] = useState([]);
const [showProcessoDropdown, setShowProcessoDropdown] = useState(false);
const [allProcessos, setAllProcessos] = useState([]);
const [editingProcesso, setEditingProcesso] = useState(false);
const [editingProcessoText, setEditingProcessoText] = useState('');
  
  // Estados - Modal de Avaliadores
  const [avaliadorModalVisible, setAvaliadorModalVisible] = useState(false);
  const [avaliadorModalType, setAvaliadorModalType] = useState(1); // 1 ou 2
  
  // Estados de interface
  const [activeTab, setActiveTab] = useState('create');
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', message: '', actions: [] });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Estados para sincronização com a nuvem
  const [syncingForms, setSyncingForms] = useState(false);
  const [serverFormsIds, setServerFormsIds] = useState([]);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncError, setSyncError] = useState(null);


  // Estados para emails das lideranças
const [emailInput, setEmailInput] = useState('');
const [emailsLiderancas, setEmailsLiderancas] = useState([]);
const [emailError, setEmailError] = useState('');
  
  // Refs
  const scrollRef = useRef(null);
  
  // Estado para edição de formulário
  const [editingFormId, setEditingFormId] = useState(null);

  // Carrega formulários do servidor ao iniciar
  useEffect(() => {
    loadServerForms();
  }, []);


// Carregar todos os processos únicos dos formulários
useEffect(() => {
  loadUniqueProcessos();
}, [customForms]);

// Função para carregar processos únicos de todos os formulários
const loadUniqueProcessos = async () => {
  try {
    console.log('🔄 Carregando processos únicos...');
    
    // Coletar todos os processos de todos os formulários
    const processosMap = new Map();
    
    customForms.forEach(form => {
      if (form.metadata?.processoAvaliado) {
        const processo = form.metadata.processoAvaliado;
        // Usar o ID como chave para evitar duplicatas
        if (!processosMap.has(processo.id)) {
          processosMap.set(processo.id, processo);
        }
      }
    });
    
    // Converter para array e ordenar alfabeticamente
    const processosArray = Array.from(processosMap.values())
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
    
    setAllProcessos(processosArray);
    console.log(`✅ ${processosArray.length} processos únicos carregados`);
    
  } catch (error) {
    console.error('❌ Erro ao carregar processos:', error);
  }
};

// Função para gerar novo ID de processo
const generateProcessoId = () => {
  // Encontrar o maior ID existente
  let maxId = 0;
  allProcessos.forEach(processo => {
    const idNum = parseInt(processo.id.replace('ID', ''));
    if (!isNaN(idNum) && idNum > maxId) {
      maxId = idNum;
    }
  });
  
  // Gerar próximo ID
  const nextId = maxId + 1;
  return `ID${String(nextId).padStart(3, '0')}`;
};

// Filtrar processos para pesquisa
useEffect(() => {
  if (!processoSearchText.trim()) {
    setFilteredProcessos([]);
    setShowProcessoDropdown(false);
    return;
  }
  
  const searchLower = processoSearchText.toLowerCase();
  const filtered = allProcessos.filter(processo => 
    processo.nome.toLowerCase().includes(searchLower) &&
    !selectedProcessos.some(p => p.id === processo.id) // Não mostrar já selecionados
  );
  
  setFilteredProcessos(filtered)
  setShowProcessoDropdown(true);
}, [processoSearchText, allProcessos, selectedProcessos]);



// Função para selecionar processo
// Função para adicionar processo à lista
const addProcesso = (processo) => {
  // Verificar se já foi adicionado
  if (selectedProcessos.some(p => p.id === processo.id)) {
    showDialog(
      'Processo já adicionado',
      'Este processo já está na lista.',
      [{ text: 'OK', action: () => setDialogVisible(false) }]
    );
    return;
  }
  
  setSelectedProcessos(prev => [...prev, processo]);
  setProcessoSearchText('');
  setShowProcessoDropdown(false);
  setEditingProcesso(false);
};

// Função para remover processo da lista
const removeProcesso = (processoId) => {
  setSelectedProcessos(prev => prev.filter(p => p.id !== processoId));
};

// Função para criar novo processo
const createNewProcesso = () => {
  if (!processoSearchText.trim()) {
    showDialog(
      'Nome Obrigatório',
      'Digite um nome para o novo processo.',
      [{ text: 'OK', action: () => setDialogVisible(false) }]
    );
    return;
  }
  
  const newProcesso = {
    id: generateProcessoId(),
    nome: processoSearchText.trim()
  };
  
  // MUDANÇA PRINCIPAL: Adicionar diretamente aos processos selecionados
  setSelectedProcessos(prev => [...prev, newProcesso]);
  
  // Manter compatibilidade
  setSelectedProcesso(newProcesso);
  
  // Limpar campo e fechar dropdown
  setProcessoSearchText('');
  setShowProcessoDropdown(false);
  
  // Adicionar à lista global
  setAllProcessos(prev => [...prev, newProcesso].sort((a, b) => 
    a.nome.localeCompare(b.nome, 'pt-BR')
  ));
};

// Função para editar processo específico
const startEditingProcessoById = (processoId) => {
  const processo = selectedProcessos.find(p => p.id === processoId);
  if (processo) {
    setEditingProcessoText(processo.nome);
    setEditingProcesso(processoId); // Guardar ID em vez de boolean
  }
};

// Função para salvar edição de processo específico
const saveProcessoEditById = async (processoId) => {
  if (!editingProcessoText.trim()) {
    showDialog(
      'Nome Obrigatório',
      'O nome do processo não pode estar vazio.',
      [{ text: 'OK', action: () => setDialogVisible(false) }]
    );
    return;
  }
  
  const processo = selectedProcessos.find(p => p.id === processoId);
  if (!processo) return;
  
  showDialog(
    'Confirmar Alteração',
    `Ao alterar o nome deste processo, todos os formulários que o utilizam serão atualizados.\n\nDeseja continuar?`,
    [
      { text: 'Cancelar', action: () => {
        setDialogVisible(false);
        setEditingProcesso(false);
        setEditingProcessoText('');
      }},
      { text: 'Confirmar', action: async () => {
        setDialogVisible(false);
        
        try {
          // Atualizar processo
          const updatedProcesso = {
            ...processo,
            nome: editingProcessoText.trim()
          };
          
          // Atualizar na lista local
          setSelectedProcessos(prev => 
            prev.map(p => p.id === processoId ? updatedProcesso : p)
          );
          
          // Atualizar na lista global
          setAllProcessos(prev => 
            prev.map(p => p.id === processoId ? updatedProcesso : p)
              .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
          );
          
          setEditingProcesso(false);
          setEditingProcessoText('');
          
          // TODO: Chamar API para atualizar no servidor
          console.log('📝 Processo atualizado:', updatedProcesso);
        } catch (error) {
          console.error('❌ Erro ao atualizar processo:', error);
          showSnackbar('Erro ao atualizar processo');
        }
      }}
    ]
  );
};

// Função para editar processo
const startEditingProcesso = () => {
  if (selectedProcesso) {
    setEditingProcessoText(selectedProcesso.nome);
    setEditingProcesso(true);
  }
};

// Função para salvar edição do processo
const saveProcessoEdit = () => {
  if (!editingProcessoText.trim()) {
    showDialog(
      'Nome Obrigatório',
      'O nome do processo não pode estar vazio.',
      [{ text: 'OK', action: () => setDialogVisible(false) }]
    );
    return;
  }
  
  showDialog(
    'Confirmar Alteração',
    `Ao alterar o nome deste processo, todos os formulários que o utilizam serão atualizados.\n\nDeseja continuar?`,
    [
      { text: 'Cancelar', action: () => {
        setDialogVisible(false);
        setEditingProcesso(false);
        setEditingProcessoText('');
      }},
      { text: 'Confirmar', action: () => {
        setDialogVisible(false);
        
        // Atualizar processo
        const updatedProcesso = {
          ...selectedProcesso,
          nome: editingProcessoText.trim()
        };
        
        setSelectedProcesso(updatedProcesso);
        setEditingProcesso(false);
        
        // Atualizar na lista
        setAllProcessos(prev => 
          prev.map(p => p.id === updatedProcesso.id ? updatedProcesso : p)
            .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'))
        );
        
        // TODO: Aqui você deve chamar uma API para atualizar todos os formulários
        console.log('📝 Processo atualizado:', updatedProcesso);
      }}
    ]
  );
};


  // Função para carregar formulários diretamente do servidor
  const loadServerForms = async () => {
    try {
      setSyncingForms(true);
      setSyncError(null);
      
      console.log('🔄 Carregando formulários do servidor...');
      
      // Verificar status do servidor
      const serverHealth = await api.checkServerHealth();
      if (serverHealth.status !== 'online') {
        console.warn('⚠️ Servidor offline:', serverHealth);
        setSyncError('Servidor offline. Verifique sua conexão com a internet.');
        setSyncingForms(false);
        return;
      }
      
      // Buscar formulários do servidor
      const serverForms = await api.getForms();
      console.log(`✅ ${serverForms.length} formulários encontrados no servidor`);
      
      // Atualizar estado com formulários do servidor
      setCustomForms(serverForms);
      
      // Extrair IDs para comparação
      const idsFromServer = serverForms.map(form => form.id);
      setServerFormsIds(idsFromServer);
      
      setLastSyncTime(new Date());
      
    } catch (error) {
      console.error('❌ Erro ao carregar formulários do servidor:', error);
      setSyncError(`Erro de sincronização: ${error.message || 'Desconhecido'}`);
    } finally {
      setSyncingForms(false);
    }
  };

  // Função para tratar a mudança de aba
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    
    // Se estiver mudando para a aba de gerenciamento, sincronizar com o servidor
    if (tab === 'manage') {
      loadServerForms();
    }
  };

  // Função auxiliar para obter a contagem correta de formulários
  const getFormCount = () => {
    return customForms.length;
  };

  // Sincronizar com o servidor
  const syncWithServer = async () => {
    await loadServerForms();
  };

  // Filtrar unidades organizacionais
  useEffect(() => {
    if (!unidadeSearchText.trim()) {
      setFilteredUnidades([]);
      setShowUnidadeDropdown(false);
      return;
    }
    
    const filtered = searchUnidades(unidadeSearchText);
    
    // Filtra unidades que já estão selecionadas
    const filteredWithoutSelected = filtered.filter(
      unidade => !selectedUnidades.some(selected => selected.id === unidade.id)
    );
    
    setFilteredUnidades(filteredWithoutSelected.slice(0, 10));
    setShowUnidadeDropdown(filteredWithoutSelected.length > 0);
  }, [unidadeSearchText, selectedUnidades]);

  // Função de pesquisa melhorada com debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    if (!searchText.trim()) {
      setFilteredRequirements(ALL_REQUIREMENTS);
      setExpandedSections({});
      setIsSearching(false);
      setSearchTerms([]);
      setSearchResultMessage('');
      return;
    }
    
    setIsSearching(true);
    
    // Delay para melhorar a performance e UX
    searchTimeoutRef.current = setTimeout(() => {
      // Processa os termos de pesquisa
      const processedTerms = processSearchTerms(searchText);
      setSearchTerms(processedTerms);
      
      if (processedTerms.length === 0) {
        setFilteredRequirements(ALL_REQUIREMENTS);
        setExpandedSections({});
        setIsSearching(false);
        setSearchResultMessage('');
        return;
      }
      
      // Aplica filtro com múltiplos termos (AND)
      const filtered = ALL_REQUIREMENTS.filter(req => {
        // Normaliza todos os campos pesquisáveis
        const normalizeText = (text) => {
          if (!text) return '';
          return text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        };
        
        // Campos a serem pesquisados em cada requisito
        const searchFields = [
          normalizeText(req.codigo),
          normalizeText(req.question),
          normalizeText(req.premissa),
          normalizeText(req.fontes),
          normalizeText(req.segmento),
          normalizeText(req.sectionInfo?.tipo),
          normalizeText(req.sectionInfo?.categoria),
          normalizeText(req.sectionInfo?.subcategoria)
        ].join(' ');
        
        // Verificar se TODOS os termos estão presentes (AND)
        return processedTerms.every(term => {
          const normalizedTerm = term.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return searchFields.includes(normalizedTerm);
        });
      });
      
      setFilteredRequirements(filtered);
      
      // Expandir automaticamente seções com resultados
      const sectionsToExpand = {};
      const groupedResults = getGroupedRequirements(filtered);
      groupedResults.forEach(section => {
        sectionsToExpand[section.key] = true;
      });
      setExpandedSections(sectionsToExpand);
      
      // Criar mensagem de resultado
      let resultMessage = '';
      if (filtered.length > 0) {
        if (processedTerms.length > 1) {
          resultMessage = `Pesquisa realizada, as seções que contêm os termos "${processedTerms.join('" E "')}" foram expandidas automaticamente`;
        } else {
          resultMessage = `Pesquisa realizada, as seções que contêm o termo "${processedTerms[0]}" foram expandidas automaticamente`;
        }
      } else {
        resultMessage = 'Nenhum resultado encontrado para os termos de pesquisa';
      }
      
      setSearchResultMessage(resultMessage);
      setIsSearching(false);
    }, 500); // 500ms de delay para dar sensação de resposta sem travar a UI
    
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchText]);

  // Processa texto de pesquisa em termos individuais para pesquisa AND
  const processSearchTerms = (text) => {
    if (!text) return [];
    
    // Remove acentos e converte para minúsculas
    const normalized = text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    
    // Divide por espaços e remove palavras curtas/conectores
    const words = normalized.split(/\s+/);
    const filteredWords = words.filter(word => {
      // Remove palavras de conexão comuns e palavras muito curtas
      const connectors = ['e', 'ou', 'de', 'da', 'do', 'das', 'dos', 'em', 'no', 'na', 'para', 'por', 'com', 'sem'];
      return word.length > 1 && !connectors.includes(word);
    });
    
    // Retorna as palavras originais para manter os acentos para display
    // mas normaliza para a busca
    const originalWords = text.toLowerCase().split(/\s+/).filter(word => {
      const normalized = word.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      const connectors = ['e', 'ou', 'de', 'da', 'do', 'das', 'dos', 'em', 'no', 'na', 'para', 'por', 'com', 'sem'];
      return normalized.length > 1 && !connectors.includes(normalized);
    });
    
    return originalWords;
  };

  // Formatar input de data
  const formatDateInput = (text) => {
    const numbers = text.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else if (numbers.length <= 4) {
      return `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
    } else {
      return `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
    }
  };

  // Formatar input de horário
  const formatTimeInput = (text) => {
    const numbers = text.replace(/\D/g, '');
    
    if (numbers.length <= 2) {
      return numbers;
    } else {
      return `${numbers.slice(0, 2)}:${numbers.slice(2, 4)}`;
    }
  };

  // Validar formato de data
  const validateDateFormat = (dateString) => {
    if (!dateString) return false;
    const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    if (!regex.test(dateString)) return false;
    
    const [, day, month, year] = dateString.match(regex);
    const date = new Date(year, month - 1, day);
    return date.getDate() == day && date.getMonth() == month - 1 && date.getFullYear() == year;
  };

  // Validar formato de email
const validateEmailFormat = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

// Adicionar email à lista
const addEmailLideranca = () => {
  if (!emailInput.trim()) {
    setEmailError('Digite um email');
    return;
  }
  
  if (!validateEmailFormat(emailInput.trim())) {
    setEmailError('Email inválido');
    return;
  }
  
  if (emailsLiderancas.includes(emailInput.trim())) {
    setEmailError('Email já adicionado');
    return;
  }
  
  if (emailsLiderancas.length >= 10) {
    setEmailError('Máximo de 10 emails permitido');
    return;
  }
  
  setEmailsLiderancas(prev => [...prev, emailInput.trim()]);
  setEmailInput('');
  setEmailError('');
};

// Remover email da lista
const removeEmailLideranca = (emailToRemove) => {
  setEmailsLiderancas(prev => prev.filter(email => email !== emailToRemove));
};  
  // Validar formato de horário
  const validateTimeFormat = (timeString) => {
    if (!timeString) return false;
    const regex = /^(\d{2}):(\d{2})$/;
    if (!regex.test(timeString)) return false;
    
    const [, hour, minute] = timeString.match(regex);
    return hour >= 0 && hour <= 23 && minute >= 0 && minute <= 59;
  };

  // Gerar data de hoje
  const getTodayDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Gerar horário atual
  const getCurrentTime = () => {
    const now = new Date();
    const hour = String(now.getHours()).padStart(2, '0');
    const minute = String(now.getMinutes()).padStart(2, '0');
    return `${hour}:${minute}`;
  };

  // Função genérica para mostrar dialogs
  const showDialog = (title, message, actions = []) => {
    const defaultActions = [
      { text: 'Cancelar', action: () => setDialogVisible(false) },
      { text: 'Confirmar', action: () => setDialogVisible(false) }
    ];

    setDialogContent({ 
      title, 
      message, 
      actions: actions.length > 0 ? actions : defaultActions
    });
    setDialogVisible(true);
  };

  // Função para exibir diálogo de sucesso
  const showSuccessDialog = (form) => {
    const nomeAvaliador1 = getAvaliadorNome(form.metadata.avaliador1);
    const nomeAvaliador2 = getAvaliadorNome(form.metadata.avaliador2);
    
    const unidadesMsg = form.metadata.unidadesOrganizacionais.length === 1 
      ? `Unidade: ${form.metadata.unidadesOrganizacionais[0].codigo}` 
      : `Unidades: ${form.metadata.unidadesOrganizacionais.length} unidades selecionadas`;
    
    showDialog(
      'Roteiro Salvo!',
      `"${form.title}" foi salvo e enviado para o servidor com ${form.totalQuestions} requisito(s).\n\nAvaliadores: ${nomeAvaliador1} e ${nomeAvaliador2}\nData/Hora: ${form.metadata.dataAvaliacao} às ${form.metadata.horarioAvaliacao}\nContato: ${form.metadata.contatoAvaliacao}\nLocal: ${form.metadata.localAvaliacao}\n${unidadesMsg}`,
      [
        { text: 'Criar Outro', action: () => { 
          setDialogVisible(false);
          resetFormFields();
        }},
        { text: 'Gerenciar Salvos', action: () => { 
          setDialogVisible(false); 
          resetFormFields();
          handleTabChange('manage'); 
        }}
      ]
    );
  };

  // Função para lidar com erro no salvamento
  const handleSaveError = (error) => {
    console.error('❌ Erro ao salvar formulário:', error);
    
    let errorMessage = 'Ocorreu um erro ao salvar o roteiro. ';
    
    // Verificar tipos específicos de erro
    if (error.message?.includes('caracteres inválidos')) {
      errorMessage = 'O roteiro contém caracteres especiais inválidos. Por favor, remova caracteres especiais do título, descrição ou campos de contato.';
    } else if (error.message?.includes('muito grande')) {
      errorMessage = 'O roteiro é muito grande para ser salvo. Tente reduzir o número de requisitos selecionados.';
    } else if (error.message?.includes('Network')) {
      errorMessage = 'Erro de conexão com o servidor. Verifique sua internet e tente novamente.';
    } else {
      errorMessage += 'Tente novamente ou contate o suporte se o problema persistir.';
    }
    
    showDialog(
      'Erro ao Salvar',
      errorMessage,
      [{ text: 'OK', action: () => setDialogVisible(false) }]
    );
  };

  // Função para lidar com erro no upload
  const handleUploadError = (error) => {
    console.error('❌ Erro no upload:', error);
    showDialog(
      'Erro no Upload',
      'Falha ao enviar roteiro para o servidor. Verifique sua conexão e tente novamente. Erro: ' + 
      (error.message || 'Desconhecido'),
      [{ text: 'OK', action: () => setDialogVisible(false) }]
    );
  };

  // Função para lidar com erro na exclusão
  const handleDeleteError = (error) => {
    console.error('❌ Erro ao excluir roteiro:', error);
    showDialog(
      'Erro',
      'Falha ao excluir roteiro. ' + (error.message || ''),
      [{ text: 'OK', action: () => setDialogVisible(false) }]
    );
  };

  // Agrupar requisitos por seção
  const getGroupedRequirements = (requirements = filteredRequirements) => {
    const grouped = {};
    
    requirements.forEach(req => {
      const key = `${req.sectionInfo.tipo} - ${req.sectionInfo.categoria}`;
      if (!grouped[key]) {
        grouped[key] = {
          key,
          tipo: req.sectionInfo.tipo,
          categoria: req.sectionInfo.categoria,
          requirements: []
        };
      }
      grouped[key].requirements.push(req);
    });
    
    return Object.values(grouped);
  };

  // Adicionar/remover requisito da seleção
  const toggleRequirement = (requirement) => {
    const isSelected = selectedRequirements.some(req => req.codigo === requirement.codigo);
    
    if (isSelected) {
      setSelectedRequirements(prev => 
        prev.filter(req => req.codigo !== requirement.codigo)
      );
    } else {
      setSelectedRequirements(prev => [...prev, requirement]);
    }
  };

  // Reordenar requisitos selecionados
  const moveRequirement = (fromIndex, toIndex) => {
    const newOrder = [...selectedRequirements];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setSelectedRequirements(newOrder);
  };

  // Remover requisito da seleção
  const removeRequirement = (codigo) => {
    setSelectedRequirements(prev => 
      prev.filter(req => req.codigo !== codigo)
    );
  };

  // Adicionar uma unidade organizacional
  const addUnidade = (unidade) => {
    if (!selectedUnidades.some(u => u.id === unidade.id)) {
      setSelectedUnidades(prev => [...prev, unidade]);
    }
    setUnidadeSearchText('');
    setShowUnidadeDropdown(false);
  };

  // Remover uma unidade organizacional
  const removeUnidade = (unidadeId) => {
    setSelectedUnidades(prev => 
      prev.filter(u => u.id !== unidadeId)
    );
  };

  // Funções de seleção de avaliadores
  const selectAvaliador1 = (avaliadorId) => {
    setAvaliador1(avaliadorId);
  };

  const selectAvaliador2 = (avaliadorId) => {
    setAvaliador2(avaliadorId);
  };

  // Abrir modal de seleção de avaliador
  const openAvaliadorSelection = (tipo) => {
    setAvaliadorModalType(tipo);
    setAvaliadorModalVisible(true);
  };

  // Selecionar avaliador do modal
  const selectAvaliadorFromModal = (avaliadorId) => {
    if (avaliadorModalType === 1) {
      selectAvaliador1(avaliadorId);
    } else {
      selectAvaliador2(avaliadorId);
    }
    setAvaliadorModalVisible(false);
  };

  // Função auxiliar - Obter avaliadores disponíveis para o modal
  const getAvailableAvaliadores = () => {
    if (avaliadorModalType === 1) {
      return AVALIADORES.filter(av => av.id !== avaliador2);
    } else {
      return AVALIADORES.filter(av => av.id !== avaliador1);
    }
  };

  // Obter nome do avaliador por ID
  const getAvaliadorNome = (avaliadorId) => {
    if (!avaliadorId) return '';
    const avaliador = AVALIADORES.find(a => a.id === avaliadorId);
    return avaliador ? avaliador.nome : 'Avaliador não encontrado';
  };

  // Obter login do avaliador por ID
  const getAvaliadorLogin = (avaliadorId) => {
    if (!avaliadorId) return '';
    const avaliador = AVALIADORES.find(a => a.id === avaliadorId);
    return avaliador ? avaliador.login : '';
  };

  // Função para resetar campos do formulário
  const resetFormFields = () => {
    console.log('🧹 Limpando todos os campos do formulário...');
    setFormTitle('');
    setFormDescription('');
    setSelectedRequirements([]);
    setSearchText('');
    setAvaliador1('');
    setAvaliador2('');
    setDataAvaliacao('');
    setHorarioAvaliacao('');
    setContatoAvaliacao('');
    setLocalAvaliacao('');
    setSelectedUnidades([]);
    setUnidadeSearchText('');
    setEditingFormId(null);
    setExpandedSections({});
    setSearchTerms([]);
    setSearchResultMessage('');
    setEmailInput('');
setEmailsLiderancas([]);
setSelectedProcessos([]);
setSelectedProcesso(null); // Manter para compatibilidade
setProcessoSearchText('');
setEditingProcesso(false);
setEditingProcessoText('');
setEmailError('');
    
    // Rolar para o topo
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ y: 0, animated: true });
    }
    
    console.log('✅ Campos limpos com sucesso');
  };

  // Validar formulário
  const validateForm = () => {
    if (!formTitle.trim()) {
      showDialog(
        'Título Obrigatório',
        'Por favor, digite um título para o roteiro.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }
    
    if (!formDescription.trim()) {
      showDialog(
        'Descrição Obrigatória',
        'Por favor, digite uma descrição para o roteiro.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }

    if (!avaliador1 || !avaliador2) {
      showDialog(
        'Avaliadores Obrigatórios',
        'Por favor, selecione dois avaliadores diferentes.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }

    if (avaliador1 === avaliador2) {
      showDialog(
        'Avaliadores Diferentes',
        'Por favor, selecione dois avaliadores diferentes.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }

    if (!dataAvaliacao || !validateDateFormat(dataAvaliacao)) {
      showDialog(
        'Data Obrigatória',
        'Por favor, digite uma data válida no formato DD/MM/AAAA.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }

    if (!horarioAvaliacao || !validateTimeFormat(horarioAvaliacao)) {
      showDialog(
        'Horário Obrigatório',
        'Por favor, digite um horário válido no formato HH:MM.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }

    if (!contatoAvaliacao.trim()) {
      showDialog(
        'Contato Obrigatório',
        'Por favor, digite as informações de contato.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }

    if (!localAvaliacao.trim()) {
      showDialog(
        'Local Obrigatório',
        'Por favor, digite o local onde será realizada a avaliação.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }

if (selectedProcessos.length === 0) {
  showDialog(
    'Processo(s) Avaliado(s) Obrigatório(s)',
    'Por favor, selecione pelo menos um processo avaliado.',
    [{ text: 'OK', action: () => setDialogVisible(false) }]
  );
  return false;
}

    if (selectedUnidades.length === 0) {
      showDialog(
        'Unidade(s) Organizacional(is) Obrigatória(s)',
        'Por favor, selecione pelo menos uma unidade organizacional.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }
    
    if (selectedRequirements.length === 0) {
      showDialog(
        'Requisitos Obrigatórios',
        'Por favor, selecione pelo menos um requisito para o roteiro.',
        [{ text: 'OK', action: () => setDialogVisible(false) }]
      );
      return false;
    }
    
    return true;
  };

  // Gerar ID único para formulário
  const generateFormId = () => {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 6);
    return `custom-form-${timestamp}-${random}`;
  };

  // Função para exibir confirmação de exclusão
  const showDeleteConfirmDialog = (formId, formTitle, onConfirm) => {
    showDialog(
      'Confirmar Exclusão',
      `Deseja realmente excluir o roteiro "${formTitle}"?\n\nEsta ação não pode ser desfeita.`,
      [
        { text: 'Cancelar', action: () => setDialogVisible(false) },
        { text: 'Excluir', action: onConfirm }
      ]
    );
  };

  // Função para exibir confirmação de exclusão bem-sucedida
  const showDeleteSuccessDialog = () => {
    showDialog(
      'Excluído',
      'Roteiro removido com sucesso.',
      [{ text: 'OK', action: () => setDialogVisible(false) }]
    );
  };

  // CORRIGIDO - Salvar formulário diretamente no servidor com melhor tratamento de erros
  const saveFormLocally = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    try {
      // Sempre gerar novo ID se não estiver editando
      let formId;
      let isEditing = false;
      
      if (editingFormId) {
        // Verificar se o formulário ainda existe no servidor
        const existingForm = customForms.find(form => form.id === editingFormId);
        if (existingForm) {
          formId = editingFormId;
          isEditing = true;
          console.log('🖊️ Editando formulário existente:', formId);
        } else {
          // Se não existe mais, criar novo
          formId = generateFormId();
          isEditing = false;
          console.log('⚠️ Formulário anterior não encontrado, criando novo:', formId);
        }
      } else {
        // Se não temos ID de edição, é sempre um novo formulário
        formId = generateFormId();
        isEditing = false;
        console.log('✨ Criando novo formulário com ID:', formId);
      }
      
      const estimatedTime = selectedRequirements.length * 3;
      
      // Criar formulário com estrutura padronizada
      const customForm = {
        id: formId,
        title: formTitle.trim(),
        description: formDescription.trim(),
        version: '01 de ' + new Date().getFullYear(),
        totalQuestions: selectedRequirements.length,
        estimatedTime,
        isCustom: true,
        createdAt: isEditing && customForms.find(f => f.id === formId)?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        status: 'published',
        author: `${getAvaliadorNome(avaliador1)} & ${getAvaliadorNome(avaliador2)}`,
        metadata: {
  avaliador1,
  avaliador2,
  dataAvaliacao,
  horarioAvaliacao,
  contatoAvaliacao,
  localAvaliacao,
  unidadesOrganizacionais: selectedUnidades.map(unidade => ({
            id: unidade.id,
            codigo: unidade.codigo,
            nome: unidade.nome,
            telefone: unidade.telefone || 'Não informado',
            email: unidade.email || 'Não informado',
            nivel: unidade.nivel || 0,
            parent: unidade.parent || null,
            searchText: unidade.searchText || `${unidade.codigo} ${unidade.nome}`
          })),
  emailsLiderancas: emailsLiderancas, // ADICIONAR ESTA LINHA
  processosAvaliados: selectedProcessos.map(p => ({ // MUDANÇA: Array de processos
  id: p.id,
  nome: p.nome
})),
// Manter compatibilidade com versão antiga
processoAvaliado: selectedProcessos.length > 0 ? selectedProcessos[0] : null,
 },
        emailsSent: [], // Inicializar array vazio para registros de emails
        requirements: selectedRequirements.map((req, index) => ({
          ...req,
          order: index + 1
        }))
      };
      
      // Sanitizar dados antes de enviar
      const sanitizedForm = sanitizeFormData(customForm);
      
      // Verificar tamanho dos dados
      const dataSize = JSON.stringify(sanitizedForm).length;
      console.log(`📏 Tamanho dos dados: ${(dataSize / 1024).toFixed(2)} KB`);
      
      if (dataSize > 1024 * 1024) { // 1MB
        throw new Error('O roteiro é muito grande para ser salvo. Tente reduzir o número de requisitos.');
      }
      
      // Transformar para formato API e enviar para MongoDB
      try {
        console.log('📤 Enviando formulário para o servidor...');
        
        // CORRIGIDO: Garantir que o formulário tenha requirements antes da transformação
        if (!sanitizedForm.requirements || sanitizedForm.requirements.length === 0) {
          throw new Error('Formulário sem requisitos para enviar');
        }
        
        // Transformar para o formato da API
        const apiFormData = transformFormToApi(sanitizedForm);
        
        // CORRIGIDO: Verificação adicional
        if (!apiFormData.questions || apiFormData.questions.length === 0) {
          console.error('❌ ERRO: Transformação falhou - nenhuma questão gerada');
          throw new Error('Erro na transformação: nenhuma questão foi gerada');
        }
        
        // Verificar se as questões foram transformadas corretamente
        console.log(`📋 Enviando ${apiFormData.questions?.length || 0} questões para o servidor`);
        
        let serverResponse;
        if (isEditing) {
          // Usar updateForm para edição
          serverResponse = await api.updateForm(formId, apiFormData);
          console.log('✅ Formulário atualizado com sucesso no servidor!');
        } else {
          // Usar createForm para novo formulário
          serverResponse = await api.createForm(apiFormData);
          console.log('✅ Formulário criado com sucesso no servidor!');
        }
        
        // Recarregar lista de formulários
        await loadServerForms();
        
        // Mostrar diálogo de sucesso
        showSuccessDialog(sanitizedForm);
        
      } catch (apiError) {
        console.error('❌ Erro ao enviar roteiro para o servidor:', apiError);
        
        // Tentar recuperar a mensagem de erro específica
        const errorMessage = apiError.message || 'Erro desconhecido ao enviar para o servidor';
        
        // Tratar erro específico
        if (errorMessage.includes('Network') || errorMessage.includes('fetch')) {
          throw new Error('Erro de conexão com o servidor. Verifique sua internet.');
        } else if (errorMessage.includes('duplicate')) {
          throw new Error('Um formulário com este ID já existe. Tente novamente.');
        } else {
          throw apiError;
        }
      }
    } catch (error) {
      handleSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  const uploadFormToCloud = async (form) => {
    setUploading(true);
    try {
      console.log('🚀 Iniciando upload do roteiro para o servidor:', form.id);
      
      // Verificar se o formulário tem requirements
      if (!form.requirements || form.requirements.length === 0) {
        console.warn('⚠️ Formulário sem requisitos para upload:', form.id);
        
        // Verificar se podemos recuperar os requisitos da versão local
        const localForm = customForms.find(f => f.id === form.id);
        if (localForm && localForm.requirements && localForm.requirements.length > 0) {
          console.log('🔄 Recuperando requisitos da versão local do formulário');
          form = {
            ...form,
            requirements: localForm.requirements
          };
        }
      }
      
      // Log adicional para verificar
      console.log(`📋 Requisitos no formulário para upload: ${form.requirements?.length || 0}`);
      
      // Transformar para formato API
      const apiFormData = transformFormToApi(form);
      
      // Verificar se as questões foram transformadas corretamente
      console.log(`📋 Questões transformadas para upload: ${apiFormData.questions?.length || 0}`);
      
      // Se não houver questões, tentar corrigir manualmente
      if (!apiFormData.questions || apiFormData.questions.length === 0) {
        console.warn('⚠️ ALERTA: Nenhuma questão transformada para upload!');
        
        if (form.requirements && form.requirements.length > 0) {
          console.log('🔧 Tentando corrigir: adicionando questions manualmente...');
          
          apiFormData.questions = form.requirements.map((req, index) => ({
            id: req.id || req.codigo,
            text: req.question,
            code: req.codigo,
            category: req.sectionInfo && req.sectionInfo.tipo && req.sectionInfo.categoria
              ? `${req.sectionInfo.tipo} - ${req.sectionInfo.categoria}`
              : 'Categoria não especificada',
            status: req.status || 'pendente',
            notes: req.premissa || '',
            attachments: [],
            premissa: req.premissa || '',
            fontes: req.fontes || '',
            segmento: req.segmento || '',
            isEssential: req.isEssential || false,
            sectionInfo: {
              tipo: req.sectionInfo?.tipo || '',
              categoria: req.sectionInfo?.categoria || '',
              subcategoria: req.sectionInfo?.subcategoria || ''
            },
            order: req.order || index + 1
          }));
          
          console.log(`🔧 Questões corrigidas: ${apiFormData.questions.length}`);
        }
      }
      
      // Garantir que o objeto questions está presente e não é undefined
      if (!apiFormData.questions) {
        apiFormData.questions = [];
      }
      
      // Enviar para o MongoDB - usar updateForm em vez de createForm para preservar o ID
      await api.updateForm(form.id, apiFormData);
      console.log('✅ Roteiro enviado com sucesso para o servidor!');
      
      // Atualizar o status localmente com padronização
      const updatedForm = { 
        ...form, 
        synced: true,
        syncedAt: new Date().toISOString()
      };
      
      const updatedCustomForms = customForms.map(f => 
        f.id === form.id ? updatedForm : f
      );
      
      // Atualizar estado em memória
      setCustomForms(updatedCustomForms);
      
      // Atualizar a lista de IDs de formulários no servidor
      if (!serverFormsIds.includes(form.id)) {
        setServerFormsIds(prev => [...prev, form.id]);
      }
      
      showDialog(
        'Upload Concluído!',
        `"${form.title}" foi enviado para o servidor e agora está disponível no MongoDB!`,
        [{ text: 'Excelente!', action: () => setDialogVisible(false) }]
      );
    } catch (error) {
      handleUploadError(error);
      
      // Marcar explicitamente como não sincronizado em caso de erro
      const updatedForm = { 
        ...form, 
        synced: false,
        syncedAt: null
      };
      
      const updatedCustomForms = customForms.map(f => 
        f.id === form.id ? updatedForm : f
      );
      
      // Atualizar estado em memória
      setCustomForms(updatedCustomForms);
    } finally {
      setUploading(false);
    }
  };

  // Deletar formulário customizado do servidor
  const deleteCustomForm = async (formId, formTitle) => {
    showDeleteConfirmDialog(formId, formTitle, async () => {
      try {
        setSaving(true);
        
        // Tentar remover do servidor
        try {
          await api.deleteForm(formId);
          console.log('✅ Roteiro removido com sucesso do servidor!');
          
          // Atualizar a lista de IDs no servidor após exclusão bem-sucedida
          setServerFormsIds(prev => prev.filter(id => id !== formId));
          
        } catch (apiError) {
          console.error('❌ Erro ao remover roteiro do servidor:', apiError);
          Alert.alert(
            'Atenção',
            'Ocorreu um erro ao excluir o roteiro do servidor.',
            [{ text: 'OK' }]
          );
          return; // Não continuar com a exclusão local se falhar no servidor
        }
        
        // Remover da lista em memória
        const updatedCustomForms = customForms.filter(f => f.id !== formId);
        setCustomForms(updatedCustomForms);
        
        showDeleteSuccessDialog();
      } catch (error) {
        handleDeleteError(error);
      } finally {
        setSaving(false);
      }
    });
  };

  // Carregar formulário para edição
  const loadFormForEdit = async (form) => {
    console.log('🔄 Carregando formulário para edição:', form.id);
    
    // Limpar qualquer estado anterior primeiro
    resetFormFields();
    
    // Aguardar um momento para garantir que o estado foi limpo
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Verificar com o servidor se o formulário ainda existe, se estiver marcado como sincronizado
    if (form.synced) {
      try {
        // Verificar se o servidor está online
        const serverHealth = await api.checkServerHealth();
        if (serverHealth.status === 'online') {
          // Tentar obter o formulário mais recente do servidor
          const serverForm = await api.getFormById(form.id);
          
          if (serverForm) {
            console.log('✅ Formulário encontrado no servidor, usando versão mais recente');
            form = serverForm;
          } else {
            console.warn('⚠️ Formulário não encontrado no servidor, usando versão local');
            // Atualizar status de sincronização
            form.synced = false;
            form.syncedAt = null;
            
            // Atualizar na lista local
            const updatedForms = customForms.map(f => 
              f.id === form.id ? { ...f, synced: false, syncedAt: null } : f
            );
            setCustomForms(updatedForms);
            
            // Atualizar lista de IDs no servidor
            setServerFormsIds(prev => prev.filter(id => id !== form.id));
          }
        }
      } catch (error) {
        console.warn('⚠️ Não foi possível verificar o formulário no servidor:', error);
      }
    }
    
    // Registrar o ID do formulário em edição
    setEditingFormId(form.id);
    
    // Configurar os campos básicos do formulário
    setFormTitle(form.title);
    setFormDescription(form.description);
    
    // Verificar se o formulário tem requirements
    let requirementsToLoad = [];
    
    if (form.requirements && Array.isArray(form.requirements) && form.requirements.length > 0) {
      console.log('✅ Formulário já possui requisitos localmente:', form.requirements.length);
      requirementsToLoad = form.requirements;
    } else {
      console.log('⚠️ Formulário sem requisitos locais. Tentando buscar do servidor...');
      
      try {
        // Tentar buscar do servidor usando o API
        const serverForm = await api.getFormById(form.id);
        
        if (serverForm && serverForm.requirements && serverForm.requirements.length > 0) {
          console.log('✅ Requisitos recuperados do servidor:', serverForm.requirements.length);
          requirementsToLoad = serverForm.requirements;
          
          // Atualizar o formulário local com os requisitos do servidor
          const updatedForms = customForms.map(f => 
            f.id === form.id ? { ...f, requirements: serverForm.requirements } : f
          );
          setCustomForms(updatedForms);
        } else {
          console.warn('⚠️ Não foi possível recuperar requisitos do servidor');
        }
      } catch (error) {
        console.error('❌ Erro ao buscar formulário do servidor:', error);
      }
    }
    
    // Definir os requisitos para edição
    if (requirementsToLoad.length > 0) {
      console.log('✅ Carregando', requirementsToLoad.length, 'requisitos para edição');
      setSelectedRequirements(requirementsToLoad);
    } else {
      console.warn('⚠️ Nenhum requisito disponível para carregar');
      setSelectedRequirements([]);
    }
    
    // Limpar a pesquisa
    setSearchText('');
    
    // Carregar metadados se disponíveis
    if (form.metadata) {
      setAvaliador1(form.metadata.avaliador1 || '');
      setAvaliador2(form.metadata.avaliador2 || '');
      setDataAvaliacao(form.metadata.dataAvaliacao || '');
      setHorarioAvaliacao(form.metadata.horarioAvaliacao || '');
      setContatoAvaliacao(form.metadata.contatoAvaliacao || '');
      setLocalAvaliacao(form.metadata.localAvaliacao || '');
      
      // Compatibilidade com versões antigas e novas de unidades organizacionais
      if (form.metadata.unidadesOrganizacionais && Array.isArray(form.metadata.unidadesOrganizacionais)) {
        setSelectedUnidades(form.metadata.unidadesOrganizacionais);
      } else if (form.metadata.unidadeOrganizacional) {
        setSelectedUnidades([form.metadata.unidadeOrganizacional]);
      } else {
        setSelectedUnidades([]);
      }
      
      setUnidadeSearchText('');

      // Carregar emails das lideranças
if (form.metadata.emailsLiderancas && Array.isArray(form.metadata.emailsLiderancas)) {
  setEmailsLiderancas(form.metadata.emailsLiderancas);
} else {
  setEmailsLiderancas([]);
}

// Carregar processos avaliados
if (form.metadata.processosAvaliados && Array.isArray(form.metadata.processosAvaliados)) {
  setSelectedProcessos(form.metadata.processosAvaliados);
} else if (form.metadata.processoAvaliado) {
  // Compatibilidade com versão antiga
  setSelectedProcessos([form.metadata.processoAvaliado]);
} else {
  setSelectedProcessos([]);
}

    }
    
    // Mudar para a aba de criação
    setActiveTab('create');
    
    // Rolar para o topo
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ y: 0, animated: true });
    }
    
    console.log('✅ Formulário carregado para edição com sucesso');
  };

  // Expandir/colapsar seção
  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Formatear data
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Função de força sincronização (botão manual)
  const forceSyncWithServer = async () => {
    try {
      // Mostrar mensagem de confirmação
      showDialog(
        'Sincronizar com Servidor',
        'Deseja verificar quais formulários realmente existem no servidor? Isso atualizará o status de sincronização.',
        [
          { text: 'Cancelar', action: () => setDialogVisible(false) },
          { 
            text: 'Sincronizar', 
            action: async () => {
              setDialogVisible(false);
              await loadServerForms();
            } 
          }
        ]
      );
    } catch (error) {
      console.error('❌ Erro ao iniciar sincronização:', error);
    }
  };

  return (
    <View style={styles.container}>
      <Appbar.Header style={styles.appbar}>
        <Appbar.BackAction 
          onPress={onBack} 
          color={AVAQualisTheme.colors.onPrimary}
        />
        <Appbar.Content 
          title="Criador de Roteiros Customizados" 
          subtitle={activeTab === 'create' ? 'Monte seu roteiro personalizado' : 'Gerencie roteiros salvos'}
          titleStyle={styles.appbarTitle}
          subtitleStyle={styles.appbarSubtitle}
        />
        <Appbar.Action 
          icon={activeTab === 'create' ? 'view-list' : 'plus-circle'}
          onPress={() => handleTabChange(activeTab === 'create' ? 'manage' : 'create')}
          iconColor={AVAQualisTheme.colors.onPrimary}
        />
      </Appbar.Header>

      {/* Abas de Navegação */}
      <View style={styles.tabContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'create' && styles.activeTab]}
          onPress={() => handleTabChange('create')}
        >
          <Text style={[styles.tabText, activeTab === 'create' && styles.activeTabText]}>
            Criar Roteiro
          </Text>
          {selectedRequirements.length > 0 && (
            <Badge style={styles.tabBadge}>{selectedRequirements.length}</Badge>
          )}
        </TouchableOpacity>
        
       <TouchableOpacity
          style={[styles.tab, activeTab === 'manage' && styles.activeTab]}
          onPress={() => handleTabChange('manage')}
        >
          <Text style={[styles.tabText, activeTab === 'manage' && styles.activeTabText]}>
            Gerenciar ({getFormCount()})
          </Text>
        </TouchableOpacity>
      </View>

      <ScrollView 
        ref={scrollRef}
        style={styles.content}
        showsVerticalScrollIndicator={true}
      >
        {activeTab === 'create' ? (
          <>




            {/* Seção: Metadados do Formulário */}
<View style={styles.metadataContainer}>
  <Card style={styles.metadataCard}>
    <Card.Content>
      <Title style={styles.sectionTitle}>Metadados da Avaliação</Title>
      
<Text style={styles.modalLabel}>⚙️ Processo(s) Avaliado(s) *:</Text>
<View style={styles.processoContainer}>
  <View style={styles.processoDropdownContainer}>
    <GrayLabelTextInput
      label="Buscar ou criar novo processo"
      value={processoSearchText}
      onChangeText={setProcessoSearchText}
      mode="outlined"
      style={styles.processoInput}
      placeholder="Digite o nome do processo..."
      placeholderTextColor="#999"
      right={
        processoSearchText ? (
          <TextInput.Icon 
            icon="close" 
            onPress={() => setProcessoSearchText('')}
          />
        ) : null
      }
    />
    
    {/* Dropdown de resultados */}
    {showProcessoDropdown && (
      <View style={[
        styles.processoDropdownList,
        { 
          zIndex: 999999, 
          elevation: 100,
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
        }
      ]}>
        <ScrollView nestedScrollEnabled maxHeight={250}>
          {filteredProcessos.length > 0 ? (
            filteredProcessos.map((processo) => (
              <TouchableOpacity
                key={processo.id}
                style={styles.processoDropdownItem}
                onPress={() => addProcesso(processo)}
              >
                <View style={styles.processoDropdownHeader}>
                  <Text style={styles.processoDropdownText}>
                    {processo.nome}
                  </Text>
                  <Text style={styles.processoDropdownId}>
                    {processo.id}
                  </Text>
                </View>
              </TouchableOpacity>
            ))
          ) : (
            <View style={styles.noProcessoContainer}>
  <Text style={styles.noProcessoText}>
    {allProcessos.length === 0 ? 
      "Ainda nenhum processo foi criado" : 
      "Nenhum processo encontrado"
    }
  </Text>
  <Text style={styles.noProcessoSubtext}>
    Clique abaixo para criar e adicionar automaticamente
  </Text>
  <Button
    mode="contained"
    onPress={createNewProcesso}
    icon="plus-circle"
    style={{ marginTop: 8 }}
    disabled={!processoSearchText.trim()}
  >
    Criar e adicionar: "{processoSearchText}"
  </Button>
</View>
          )}
        </ScrollView>
      </View>
    )}
    
    {/* Processos selecionados */}
    {selectedProcessos.length > 0 && (
      <View style={styles.processosSelectedContainer}>
        <View style={styles.processosSelectedHeader}>
          <Text style={styles.processosSelectedTitle}>
            Processos selecionados:
          </Text>
          <View style={styles.processosSelectedCount}>
            <Text style={styles.processosSelectedCountText}>
              {selectedProcessos.length}
            </Text>
          </View>
        </View>
        
        <ScrollView 
          style={styles.processosScrollContainer}
          nestedScrollEnabled
          showsVerticalScrollIndicator={true}
        >
          {selectedProcessos.map((processo, index) => (
            <View key={processo.id} style={styles.processoSelectedCard}>
              <View style={styles.processoSelectedHeader}>
                <View style={styles.processoSelectedInfo}>
                  {editingProcesso === processo.id ? (
                    <TextInput
                      value={editingProcessoText}
                      onChangeText={setEditingProcessoText}
                      style={styles.processoEditInput}
                      autoFocus
                    />
                  ) : (
                    <>
                      <Text style={styles.processoSelectedNome}>
                        {index + 1}. {processo.nome}
                      </Text>
                      <Text style={styles.processoSelectedId}>
                        {processo.id}
                      </Text>
                    </>
                  )}
                </View>
                
                <View style={styles.processoSelectedActions}>
                  {editingProcesso === processo.id ? (
                    <>
                      <Button
                        mode="text"
                        onPress={() => {
                          setEditingProcesso(false);
                          setEditingProcessoText('');
                        }}
                        compact
                        textColor="#666"
                      >
                        Cancelar
                      </Button>
                      <Button
                        mode="contained"
                        onPress={() => saveProcessoEditById(processo.id)}
                        compact
                      >
                        Salvar
                      </Button>
                    </>
                  ) : (
                    <>
                      <TouchableOpacity
                        style={styles.processoEditButton}
                        onPress={() => startEditingProcessoById(processo.id)}
                      >
                        <Text style={styles.processoEditButtonText}>
                          ✏️ Editar
                        </Text>
                      </TouchableOpacity>
                      <TouchableOpacity
                        style={styles.processoRemoveButton}
                        onPress={() => removeProcesso(processo.id)}
                      >
                        <Text style={styles.processoRemoveButtonText}>
                          ✕
                        </Text>
                      </TouchableOpacity>
                    </>
                  )}
                </View>
              </View>
              
              {editingProcesso === processo.id && (
                <View style={styles.processoEditWarning}>
                  <Text style={styles.processoEditWarningText}>
                    ⚠️ Ao editar o nome, todos os roteiros com este processo serão atualizados
                  </Text>
                </View>
              )}
            </View>
          ))}
        </ScrollView>
        
        {/* Botão para adicionar mais processos */}
        <TouchableOpacity 
          style={styles.addProcessoButton}
          onPress={() => {
            setProcessoSearchText('');
            // Focar no input de busca (se tiver ref)
          }}
        >
          <MaterialCommunityIcons name="plus-circle" size={20} color="#fff" />
          <Text style={styles.addProcessoButtonText}>
            Adicionar outro processo
          </Text>
        </TouchableOpacity>
      </View>
    )}
  </View>
</View>
      
      {/* Container com z-index menor para os outros campos */}
      <View style={{ zIndex: 10000 }}>
        {/* Avaliadores */}
        <View style={styles.evaluatorsSection}>
          <Text style={styles.modalLabel}>Avaliadores * (Selecione 2 diferentes):</Text>
          
          <View style={styles.evaluatorsContainer}>
            {/* Avaliador 1 */}
            <View style={styles.evaluatorCard}>
              <TouchableOpacity onPress={() => openAvaliadorSelection(1)} style={styles.evaluatorTouchable}>
                <View style={styles.evaluatorHeader}>
                  <Text style={styles.evaluatorLabel}>1º Avaliador</Text>
                </View>
                <Text style={styles.evaluatorName}>
                  {avaliador1 ? getAvaliadorNome(avaliador1) : 'Clique para selecionar'}
                </Text>
                {avaliador1 && (
                  <Text style={styles.evaluatorLogin}>
                    {getAvaliadorLogin(avaliador1)}
                  </Text>
                )}
              </TouchableOpacity>
              {avaliador1 && (
                <TouchableOpacity style={styles.removeEvaluatorButton} onPress={() => setAvaliador1('')}>
                  <Text style={styles.removeEvaluatorText}>✕</Text>
                </TouchableOpacity>
              )}
            </View>
            
            {/* Avaliador 2 */}
            <View style={styles.evaluatorCard}>
              <TouchableOpacity onPress={() => openAvaliadorSelection(2)} style={styles.evaluatorTouchable}>
                <View style={styles.evaluatorHeader}>
                  <Text style={styles.evaluatorLabel}>2º Avaliador</Text>
                </View>
                <Text style={styles.evaluatorName}>
                  {avaliador2 ? getAvaliadorNome(avaliador2) : 'Clique para selecionar'}
                </Text>
                {avaliador2 && (
                  <Text style={styles.evaluatorLogin}>
                    {getAvaliadorLogin(avaliador2)}
                  </Text>
                )}
              </TouchableOpacity>
              {avaliador2 && (
                <TouchableOpacity style={styles.removeEvaluatorButton} onPress={() => setAvaliador2('')}>
                  <Text style={styles.removeEvaluatorText}>✕</Text>
                </TouchableOpacity>
              )}
            </View>
          </View>

          {/* Seleção Rápida */}
          <View style={styles.quickSelectionContainer}>
            <Text style={styles.quickSelectionTitle}>
              Seleção Rápida (Todos os {AVALIADORES.length} avaliadores):
            </Text>
            <ScrollView 
              horizontal={false} 
              nestedScrollEnabled 
              style={styles.quickSelectionScrollView}
              showsVerticalScrollIndicator={true}
            >
              <View style={styles.quickSelectionButtons}>
               {AVALIADORES.map((av) => (
                <TouchableOpacity
                  key={av.id}
                  onPress={() => {
                    if (avaliador1 === av.id) {
                      setAvaliador1('');
                    } else if (avaliador2 === av.id) {
                      setAvaliador2('');
                    } else if (!avaliador1) {
                      selectAvaliador1(av.id);
                    } else if (!avaliador2) {
                      selectAvaliador2(av.id);
                    } else {
                      selectAvaliador1(av.id);
                    }
                  }}
                  style={[
                    styles.quickSelectionButton,
                    (avaliador1 === av.id || avaliador2 === av.id) && styles.quickSelectionButtonSelected,
                    av.isSpecial && styles.quickSelectionButtonSpecial
                  ]}
                >
                  <Text style={[
                    styles.quickSelectionButtonText,
                    (avaliador1 === av.id || avaliador2 === av.id) && styles.quickSelectionButtonTextSelected,
                    av.isSpecial && styles.quickSelectionButtonTextSpecial
                  ]}>
                    {av.nome.split(' ')[0]} {av.nome.split(' ')[1] || ''}
                  </Text>
                </TouchableOpacity>
              ))}
              </View>
            </ScrollView>
            
            {/* Status dos Avaliadores */}
            <View style={styles.evaluatorsStatus}>
              <Text style={styles.evaluatorsStatusText}>
                Avaliadores Selecionados: 
                {avaliador1 ? ` 1º: ${getAvaliadorNome(avaliador1).split(' ').slice(0, 2).join(' ')}` : ' 1º: (vazio)'}
                {avaliador2 ? ` | 2º: ${getAvaliadorNome(avaliador2).split(' ').slice(0, 2).join(' ')}` : ' | 2º: (vazio)'}
              </Text>
            </View>
          </View>
        </View>

        {/* Data e Horário */}
        <Text style={styles.modalLabel}>Data da Avaliação *:</Text>
        <View style={styles.dateTimeContainer}>
          <View style={styles.dateInputContainer}>
            <GrayLabelTextInput
              label="Data"
              value={dataAvaliacao}
              onChangeText={(text) => setDataAvaliacao(formatDateInput(text))}
              mode="outlined"
              style={styles.dateInput}
              placeholder="DD/MM/AAAA"
              placeholderTextColor="#999"
              keyboardType="numeric"
              maxLength={10}
            />
            <TouchableOpacity 
              style={styles.calendarButton}
              onPress={() => setDataAvaliacao(getTodayDate())}
            >
              <Text style={styles.calendarButtonText}>📅</Text>
            </TouchableOpacity>
          </View>
          
          <View style={styles.timeInputContainer}>
            <GrayLabelTextInput
              label="Horário"
              value={horarioAvaliacao}
              onChangeText={(text) => setHorarioAvaliacao(formatTimeInput(text))}
              mode="outlined"
              style={styles.timeInput}
              placeholder="HH:MM"
              placeholderTextColor="#999"
              keyboardType="numeric"
              maxLength={5}
            />
            <TouchableOpacity 
              style={styles.clockButton}
              onPress={() => setHorarioAvaliacao(getCurrentTime())}
            >
              <Text style={styles.clockButtonText}>⏰</Text>
            </TouchableOpacity>
          </View>
        </View>

        {/* Contato e Local */}
        <Text style={styles.modalLabel}>📞 Contato e 📍 Local da Avaliação *:</Text>
        <View style={styles.contactLocationContainer}>
          <View style={styles.contactInputContainer}>
            <GrayLabelTextInput
              label="📞 Contato Responsável"
              value={contatoAvaliacao}
              onChangeText={setContatoAvaliacao}
              mode="outlined"
              style={styles.contactInput}
              placeholder="Dra Silva (Ramal: 0123)"
              placeholderTextColor="#999"
              maxLength={100}
            />
          </View>
          
          <View style={styles.locationInputContainer}>
            <GrayLabelTextInput
              label="📍 Local da Avaliação"
              value={localAvaliacao}
              onChangeText={setLocalAvaliacao}
              mode="outlined"
              style={styles.locationInput}
              placeholder="Sala XYZ do Bloco 8DJU"
              placeholderTextColor="#999"
              maxLength={100}
            />
          </View>
        </View>

        {/* Emails das Lideranças */}
        <Text style={styles.modalLabel}>📧 Emails das Lideranças (Opcional - Máx. 10):</Text>
        <View style={styles.emailsContainer}>
          <View style={styles.emailInputContainer}>
            <GrayLabelTextInput
              label="Email da Liderança"
              value={emailInput}
              onChangeText={(text) => {
                setEmailInput(text);
                setEmailError('');
              }}
              mode="outlined"
              style={styles.emailInput}
              placeholder="lideranca@ebserh.gov.br"
              placeholderTextColor="#999"
              keyboardType="email-address"
              autoCapitalize="none"
              maxLength={100}
              error={!!emailError}
            />
            <TouchableOpacity 
              style={[
                styles.addEmailButton,
                (!emailInput.trim() || emailsLiderancas.length >= 10) && styles.addEmailButtonDisabled
              ]}
              onPress={addEmailLideranca}
              disabled={!emailInput.trim() || emailsLiderancas.length >= 10}
            >
              <Text style={styles.addEmailButtonText}>➕ Adicionar</Text>
            </TouchableOpacity>
          </View>
          
          {emailError && (
            <Text style={styles.emailErrorText}>❌ {emailError}</Text>
          )}
          
          {emailsLiderancas.length > 0 && (
            <View style={styles.emailsList}>
              <Text style={styles.emailsListTitle}>
                📧 {emailsLiderancas.length} email{emailsLiderancas.length !== 1 ? 's' : ''} adicionado{emailsLiderancas.length !== 1 ? 's' : ''}:
              </Text>
              {emailsLiderancas.map((email, index) => (
                <View key={email} style={styles.emailItem}>
                  <Text style={styles.emailItemText}>
                    {index + 1}. {email}
                  </Text>
                  <TouchableOpacity
                    style={styles.removeEmailButton}
                    onPress={() => removeEmailLideranca(email)}
                  >
                    <Text style={styles.removeEmailButtonText}>✕</Text>
                  </TouchableOpacity>
                </View>
              ))}
            </View>
          )}
          
          <Text style={styles.emailsHelpText}>
            💡 Adicione emails das lideranças que receberão notificações sobre esta avaliação
          </Text>
        </View>

        {/* Múltiplas Unidades Organizacionais */}
        <Text style={styles.modalLabel}>🏢 Unidades Organizacionais Avaliadas *:</Text>
        <View style={styles.unitDropdownContainer}>
          <GrayLabelTextInput
            label="Buscar Unidade Organizacional"
            value={unidadeSearchText}
            onChangeText={setUnidadeSearchText}
            mode="outlined"
            style={styles.unitInput}
            placeholder="Digite para buscar e adicionar unidades..."
            placeholderTextColor="#999"
            right={
              unidadeSearchText ? (
                <TextInput.Icon 
                  icon="close" 
                  onPress={() => setUnidadeSearchText('')}
                />
              ) : null
            }
          />
          
          {/* Dropdown para resultados da busca */}
          {showUnidadeDropdown && (
            <View style={styles.unitDropdownList}>
              <ScrollView nestedScrollEnabled maxHeight={300}>
                {filteredUnidades.map((unidade) => (
                  <TouchableOpacity
                    key={unidade.id}
                    style={styles.unitDropdownItem}
                    onPress={() => addUnidade(unidade)}
                  >
                    <Text style={styles.unitDropdownText}>
                      {unidade.codigo} - {unidade.nome}
                    </Text>
                    <Text style={styles.unitDropdownSubtext}>
                      📞{unidade.telefone} • 📧{unidade.email}
                    </Text>
                    <Text style={styles.unitDropdownHierarchy}>
                      🏗️ Hierarquia: {getUnidadeHierarchy(unidade.id)}
                    </Text>
                  </TouchableOpacity>
                ))}
              </ScrollView>
            </View>
          )}
          
          {/* Contador de unidades selecionadas */}
          {selectedUnidades.length > 0 && (
            <View style={styles.unidadesCounter}>
              <Text style={styles.unidadesCounterText}>
                🏢 {selectedUnidades.length} unidade{selectedUnidades.length !== 1 ? 's' : ''} selecionada{selectedUnidades.length !== 1 ? 's' : ''}
              </Text>
            </View>
          )}
          
          {/* Visualização de unidades organizacionais */}
          {selectedUnidades.length > 0 && (
            <UnidadesOrgDisplay unidades={selectedUnidades} />
          )}
          
          {/* Lista de unidades selecionadas - Visualização para edição com botão de remover */}
          {selectedUnidades.length > 0 && (
            <View style={styles.selectedUnidadesList}>
              <ScrollView 
                nestedScrollEnabled={true}
                style={styles.selectedUnidadesScroll}
                showsVerticalScrollIndicator={true}
              >
                {selectedUnidades.map((unidade, index) => (
                  <View key={unidade.id} style={styles.selectedUnidadeCard}>
                    <View style={styles.selectedUnidadeHeader}>
                      <Text style={styles.selectedUnidadeIndex}>{index + 1}</Text>
                      <Text style={styles.selectedUnidadeCode}>
                        {unidade.codigo}
                      </Text>
                      <TouchableOpacity 
                        style={styles.removeUnidadeButton} 
                        onPress={() => removeUnidade(unidade.id)}
                      >
                        <Text style={styles.removeUnidadeText}>✕</Text>
                      </TouchableOpacity>
                    </View>
                    
                    <Text style={styles.selectedUnidadeName}>
                      {unidade.nome}
                    </Text>
                    
                    <Text style={styles.selectedUnidadeDetails}>
                      📞 {unidade.telefone} • 📧 {unidade.email}
                    </Text>
                    
                    <Text style={styles.selectedUnidadeHierarchy}>
                      🏗️ {getUnidadeHierarchy(unidade.id)}
                    </Text>
                  </View>
                ))}
              </ScrollView>
            </View>
          )}
        </View>
      </View>
    </Card.Content>
  </Card>
</View>

            {/* Seções com Z-Index menor */}
            <View style={styles.lowZIndexContainer}>
              {/* Informações do Roteiro */}
              <Card style={styles.sectionCard}>
                <Card.Content>
                  <Title style={styles.sectionTitle}>📝 Informações do Roteiro</Title>
                  
                  <GrayLabelTextInput
                    label="Título do Roteiro"
                    value={formTitle}
                    onChangeText={setFormTitle}
                    mode="outlined"
                    style={styles.input}
                    placeholder="Ex: Avaliação Rápida de Segurança do Paciente"
                    placeholderTextColor="#999"
                    maxLength={100}
                  />
                  
                  <GrayLabelTextInput
                    label="Descrição do Roteiro"
                    value={formDescription}
                    onChangeText={setFormDescription}
                    mode="outlined"
                    multiline
                    numberOfLines={3}
                    style={styles.input}
                    placeholder="Descreva o objetivo e contexto deste roteiro customizado..."
                    placeholderTextColor="#999"
                    maxLength={500}
                  />
                  
                  {selectedRequirements.length > 0 && (
                    <View style={styles.formSummary}>
                      <Text style={styles.summaryText}>
                        📊 Resumo: {selectedRequirements.length} requisito(s) selecionado(s)
                      </Text>
                      <Text style={styles.summaryText}>
                        ⏱️ Tempo estimado: ~{selectedRequirements.length * 3} minutos
                      </Text>
                    </View>
                  )}
                </Card.Content>
              </Card>

              {/* Pesquisa de Requisitos */}
              <Card style={styles.sectionCard}>
                <Card.Content>
                  <Title style={styles.sectionTitle}>🔍 Pesquisar Requisitos</Title>
                  
                  <GrayLabelTextInput
                    label="Buscar por código, texto, seção..."
                    value={searchText}
                    onChangeText={setSearchText}
                    mode="outlined"
                    style={styles.searchInput}
                    placeholder="Digite para filtrar requisitos..."
                    placeholderTextColor="#999"
                    left={<TextInput.Icon icon="magnify" />}
                    right={searchText ? <TextInput.Icon icon="close" onPress={() => setSearchText('')} /> : null}
                  />
                  
                  <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8 }}>
                    <Text style={styles.searchResults}>
                      📋 {filteredRequirements.length} de {ALL_REQUIREMENTS.length} requisitos encontrados
                    </Text>
                    
                    {/* Indicador de pesquisa em andamento */}
                    <SearchingIndicator isSearching={isSearching} />
                    
                    {/* Mensagem de resultado da pesquisa */}
                    {!isSearching && searchResultMessage && (
                      <View style={{ flex: 1, marginLeft: 8 }}>
                        <Text style={{ fontSize: 12, color: '#2196F3', fontStyle: 'italic' }}>
                          {searchResultMessage}
                        </Text>
                      </View>
                    )}
                  </View>
                </Card.Content>
              </Card>

              {/* Requisitos Selecionados */}
              {selectedRequirements.length > 0 && (
                <Card style={styles.selectedCard}>
                  <Card.Content>
                    <View style={styles.selectedHeader}>
                      <Title style={styles.sectionTitle}>✅ Requisitos Selecionados</Title>
                      <Badge style={styles.selectedBadge}>{selectedRequirements.length}</Badge>
                    </View>
                    
                    <Text style={styles.orderInfo}>
                      📝 A ordem abaixo será mantida no formulário final:
                    </Text>
                    
                    <ScrollView style={styles.selectedScroll} nestedScrollEnabled>
                      {selectedRequirements.map((req, index) => (
                        <View key={req.codigo} style={styles.selectedItem}>
                          <View style={styles.selectedItemHeader}>
                            <Text style={styles.selectedOrder}>{index + 1}</Text>
                            <Chip 
                              mode="flat" 
                              compact
                              style={styles.selectedChip}
                            >
                              📋 {req.codigo}
                            </Chip>
                            {req.isEssential && (
                              <Chip 
                                mode="flat" 
                                compact
                                style={styles.essentialChip}
                              >
                                ⚠️ RE
                              </Chip>
                            )}
                            <IconButton
                              icon="close"
                              size={16}
                              onPress={() => removeRequirement(req.codigo)}
                              iconColor={AVAQualisTheme.colors.error}
                              style={styles.removeButton}
                            />
                          </View>
                          <Text style={styles.selectedQuestion} numberOfLines={2}>
                            {req.question}
                          </Text>
                          
                          {/* Botões de Reordenação */}
                          <View style={styles.reorderButtons}>
                            <Button
                              mode="outlined"
                              compact
                              disabled={index === 0}
                              onPress={() => moveRequirement(index, index - 1)}
                              icon="arrow-up"
                              style={styles.reorderButton}
                            >
                              ⬆️
                            </Button>
                            <Button
                              mode="outlined"
                              compact
                              disabled={index === selectedRequirements.length - 1}
                              onPress={() => moveRequirement(index, index + 1)}
                              icon="arrow-down"
                              style={styles.reorderButton}
                            >
                              ⬇️
                            </Button>
                          </View>
                        </View>
                      ))}
                    </ScrollView>
                    
                    <Divider style={styles.divider} />
                    
                    <Button 
                      mode="contained"
                      onPress={saveFormLocally}
                      loading={saving}
                      icon="content-save"
                      style={styles.saveButton}
                      buttonColor={AVAQualisTheme.colors.success}
                      disabled={selectedRequirements.length === 0 || !formTitle.trim() || !formDescription.trim() || !avaliador1 || !avaliador2 || selectedUnidades.length === 0 || !dataAvaliacao || !horarioAvaliacao || !contatoAvaliacao.trim() || !localAvaliacao.trim() || !selectedProcesso}
                    >
                      💾 {editingFormId ? 'Atualizar' : 'Salvar'} Roteiro ({selectedRequirements.length} requisitos)
                    </Button>
                  </Card.Content>
                </Card>
              )}

              {/* Lista de Requisitos por Categoria - MELHORADA com campos adicionais */}
              <Card style={styles.requirementsCard}>
                <Card.Content>
                  <Title style={styles.sectionTitle}>📚 Todos os Requisitos Disponíveis</Title>
                  
                  {getGroupedRequirements().map((section) => (
                    <View key={section.key} style={styles.requirementSection}>
                      <TouchableOpacity
                        onPress={() => toggleSection(section.key)}
                        style={styles.sectionHeaderButton}
                      >
                        <View style={styles.sectionHeaderContent}>
                          <HighlightedText 
                            text={section.tipo}
                            terms={searchTerms}
                            style={styles.sectionHeaderTitle}
                          />
                          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                            <HighlightedText 
                              text={section.categoria}
                              terms={searchTerms}
                              style={styles.sectionHeaderSubtitle}
                            />
                            <Text style={styles.sectionHeaderSubtitle}> • {section.requirements.length} requisito(s)</Text>
                          </View>
                        </View>
                        <Text style={styles.sectionToggle}>
                          {expandedSections[section.key] ? '▲' : '▼'}
                        </Text>
                      </TouchableOpacity>
                      
                      {expandedSections[section.key] && (
                        <View style={styles.requirementsList}>
                          {section.requirements.map((req) => {
                            const isSelected = selectedRequirements.some(selected => selected.codigo === req.codigo);
                            
                            return (
                              <TouchableOpacity
                                key={req.codigo}
                                onPress={() => toggleRequirement(req)}
                                style={[
                                  styles.requirementItem,
                                  isSelected && styles.requirementItemSelected
                                ]}
                              >
                                <View style={styles.requirementHeader}>
                                  <View style={styles.requirementMeta}>
                                    <Chip 
                                      mode={isSelected ? "flat" : "outlined"}
                                      compact
                                      style={[
                                        styles.requirementChip,
                                        isSelected && styles.requirementChipSelected
                                      ]}
                                    >
                                      <Text>📋 </Text>
                                      <HighlightedText 
                                        text={req.codigo}
                                        terms={searchTerms}
                                      />
                                    </Chip>
                                    {req.isEssential && (
                                      <Chip 
                                        mode="flat" 
                                        compact
                                        style={styles.essentialChip}
                                      >
                                        ⚠️ RE
                                      </Chip>
                                    )}
                                  </View>
                                  <Text style={[
                                    styles.selectionIndicator,
                                    isSelected && styles.selectionIndicatorSelected
                                  ]}>
                                    {isSelected ? '✅' : '➕'}
                                  </Text>
                                </View>
                                
                                <View style={styles.requirementContent}>
                                  <Text style={styles.requirementLabel}>REQUISITO:</Text>
                                  <HighlightedText 
                                    text={req.question}
                                    terms={searchTerms}
                                    style={styles.requirementText}
                                  />
                                </View>

                                {req.premissa && (
                                  <View style={styles.requirementContent}>
                                    <Text style={styles.requirementLabel}>PREMISSA:</Text>
                                    <HighlightedText 
                                      text={req.premissa}
                                      terms={searchTerms}
                                      style={styles.requirementPremissaText}
                                    />
                                  </View>
                                )}

                                {req.fontes && (
                                  <View style={styles.requirementContent}>
                                    <Text style={styles.requirementLabel}>FONTES DE INFORMAÇÃO:</Text>
                                    <HighlightedText 
                                      text={req.fontes}
                                      terms={searchTerms}
                                      style={styles.requirementFontesText}
                                    />
                                  </View>
                                )}

                                <View style={styles.requirementContent}>
                                  <Text style={styles.requirementLabel}>SEGMENTO:</Text>
                                  <HighlightedText 
                                    text={req.segmento}
                                    terms={searchTerms}
                                    style={styles.requirementSegmentoText}
                                  />
                                </View>
                                
                                {isSelected && (
                                  <View style={styles.selectedOrderInfo}>
                                    <Text style={styles.selectedOrderText}>
                                      📍 Posição no formulário: {selectedRequirements.findIndex(s => s.codigo === req.codigo) + 1}°
                                    </Text>
                                  </View>
                                )}
                              </TouchableOpacity>
                            );
                          })}
                        </View>
                      )}
                    </View>
                  ))}
                </Card.Content>
              </Card>
            </View>
          </>
        ) : (
          /* Aba: Gerenciar Formulários - VISUAL APRIMORADO E ATUALIZADO */
          <View style={styles.manageSection}>
            <Card style={styles.sectionCard}>
              <Card.Content>
                <Title style={styles.sectionTitle}>📁 Roteiros Customizados Salvos</Title>
                <Text style={styles.manageDescription}>
                  Gerencie seus roteiros personalizados. Os roteiros são salvos diretamente no servidor.
                </Text>
                
                {/* Status de Sincronização */}
                <View style={styles.syncStatusContainer}>
                  <View style={styles.syncStatusHeader}>
                    <Text style={styles.syncStatusTitle}>
                      Status da Sincronização
                    </Text>
                    <TouchableOpacity 
                      style={styles.syncButton}
                      onPress={forceSyncWithServer}
                      disabled={syncingForms}
                    >
                      <Text style={styles.syncButtonText}>
                        {syncingForms ? '🔄 Sincronizando...' : '🔄 Sincronizar Agora'}
                      </Text>
                    </TouchableOpacity>
                  </View>
                  
                  {lastSyncTime && (
                    <Text style={styles.lastSyncText}>
                      Última sincronização: {formatDate(lastSyncTime)}
                    </Text>
                  )}
                  
                  {syncError && (
                    <Text style={styles.syncErrorText}>
                      Erro: {syncError}
                    </Text>
                  )}
                  
                  <View style={styles.serverStatusInfo}>
                    <Text style={styles.serverStatusText}>
                      Roteiros no servidor: {serverFormsIds.length || 0}
                    </Text>
                    <Text style={styles.serverStatusText}>
                      Roteiros carregados: {customForms.length || 0}
                    </Text>
                  </View>
                </View>
                
                <Divider style={styles.divider} />
                
                {/* Legenda para ícones */}
                <View style={styles.legendContainer}>
                  <Text style={styles.legendTitle}>Legenda:</Text>
                  <View style={styles.legendItem}>
                    <Chip mode="flat" style={styles.uploadedChip} textStyle={styles.chipText} compact>
                      ☁️ Na Nuvem
                    </Chip>
                    <Text style={styles.legendText}>- Roteiro já está sincronizado com o servidor</Text>
                  </View>
                  <View style={styles.legendItem}>
                    <Chip mode="outlined" style={styles.localChip} textStyle={styles.chipText} compact>
                      📱 Local
                    </Chip>
                    <Text style={styles.legendText}>- Roteiro existe apenas localmente no dispositivo</Text>
                  </View>
                </View>
              </Card.Content>
            </Card>
            
            {/* Loading Indicator for sync */}
            <SyncLoadingIndicator syncingForms={syncingForms} />
            
            {customForms.length === 0 ? (
              <Card style={styles.emptyCard}>
                <Card.Content>
                  <View style={styles.emptyContent}>
                    <Text style={styles.emptyIcon}>📝</Text>
                    <Title style={styles.emptyTitle}>Nenhum roteiro criado</Title>
                    <Paragraph style={styles.emptyText}>
                      Você ainda não criou nenhum roteiro customizado.{'\n'}
                      Use a aba "Criar Roteiro" para começar!
                    </Paragraph>
                  </View>
                </Card.Content>
                <Card.Actions style={styles.emptyActions}>
                  <Button 
                    mode="contained" 
                    onPress={() => handleTabChange('create')}
                    icon="plus-circle"
                    buttonColor={AVAQualisTheme.colors.primary}
                  >
                    Criar Primeiro Roteiro
                  </Button>
                </Card.Actions>
              </Card>
            ) : (
              customForms.map((form) => {
                const complexity = getFormComplexity(form.totalQuestions);
                const essentialCount = countEssentialRequirements(form);
                
                // Verificar o status de sincronização
                const existsOnServer = serverFormsIds.includes(form.id);
                const syncStatusChanged = form.synced !== existsOnServer;
                
                return (
                <Card 
                  key={form.id} 
                  style={[
                    styles.formCard,
                    form.synced && styles.uploadedFormCard,
                    syncStatusChanged && styles.formCardStatusChanged
                  ]}
                >
                  <Card.Content>
                    <View style={styles.formCardHeader}>
                      <View style={styles.formCardInfo}>
                        <Title style={styles.formCardTitle}>{form.title}</Title>
                        
                        {/* DADOS DE VERSÃO E TIMESTAMPS */}
                        <View style={{flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', marginBottom: 2}}>
                          <Text style={styles.formCardDate}>
                            📅 Criado em: {formatDate(form.createdAt)}
                          </Text>
                          {form.synced && form.syncedAt && (
                            <>
                              <Text style={{fontSize: 12, color: '#666', marginHorizontal: 4}}>•</Text>
                              <Text style={styles.formCardUploadDate}>
                                ☁️ Enviado em: {formatDate(form.syncedAt)}
                              </Text>
                            </>
                          )}
                          {essentialCount > 0 && (
                            <>
                              <Text style={{fontSize: 12, color: '#666', marginHorizontal: 4}}>•</Text>
                              <Text style={{
                                fontSize: 12, 
                                fontWeight: 'bold',
                                color: '#FFC107'
                              }}>
                                ⚠️ {essentialCount} RE
                              </Text>
                            </>
                          )}
                        </View>
                      </View>
                      
                      <View style={styles.formCardStatus}>
                        {/* Verificar status real com base na lista do servidor */}
                        {existsOnServer ? (
                          <Chip 
                            mode="flat" 
                            style={[styles.uploadedChip, syncStatusChanged && styles.syncStatusChangedChip]}
                            textStyle={styles.chipText}
                            icon="cloud-check"
                            compact
                          >
                            ☁️ Na Nuvem
                          </Chip>
                        ) : (
                          <Chip 
                            mode="outlined" 
                            style={[styles.localChip, syncStatusChanged && styles.syncStatusChangedChip]}
                            textStyle={styles.chipText}
                            icon="cellphone"
                            compact
                          >
                            📱 Local
                          </Chip>
                        )}
                        
                        {/* Indicador de mudança de status */}
                        {syncStatusChanged && (
                          <Text style={styles.syncStatusChangedText}>
                            ⚠️ Status alterado
                          </Text>
                        )}
                      </View>
                    </View>
                    
                    <Paragraph style={styles.formCardDescription} numberOfLines={2}>
                      {form.description}
                    </Paragraph>
                    
                    {/* Metadados em formato responsivo */}
                    {form.metadata && (
                      <View style={styles.metadataInlineContainer}>
                        {/* Linha 1: Avaliadores */}
                        <View style={styles.metadataInlineRow}>
                          <Text style={styles.metadataInlineText}>
                            👥 Avaliadores: {getAvaliadorNome(form.metadata.avaliador1)} e {getAvaliadorNome(form.metadata.avaliador2)}
                          </Text>
                        </View>
                        
                        {/* Linha 2: Data, Hora, Contato */}
                        <View style={styles.metadataInlineRow}>
                          <Text style={styles.metadataInlineText}>
                            📅 Data/Hora: {form.metadata.dataAvaliacao} às {form.metadata.horarioAvaliacao}
                          </Text>
                          <Text style={styles.metadataInlineText}>
                            📞 Contato: {form.metadata.contatoAvaliacao}
                          </Text>
                        </View>
                        
                        {/* Linha 3: Local */}
                        <View style={styles.metadataInlineRow}>
                          {form.metadata.localAvaliacao && (
                            <Text style={styles.metadataInlineText}>
                              📍 Local: {form.metadata.localAvaliacao}
                            </Text>
                          )}
                        </View>
                        {/* Linha 4: Emails das Lideranças */}
{form.metadata.emailsLiderancas && form.metadata.emailsLiderancas.length > 0 && (
  <View style={styles.metadataInlineRow}>
    <Text style={styles.metadataInlineText}>
      📧 Emails: {form.metadata.emailsLiderancas.length} liderança{form.metadata.emailsLiderancas.length !== 1 ? 's' : ''}
    </Text>
  </View>
)}

{/* Linha 5: Processo Avaliado */}
{form.metadata.processoAvaliado && (
  <View style={styles.metadataInlineRow}>
    <Text style={styles.metadataInlineText}>
      ⚙️ Processo: {form.metadata.processoAvaliado.nome} ({form.metadata.processoAvaliado.id})
    </Text>
  </View>
)}
                        {/* Visualização de unidades organizacionais */}
                        {form.metadata.unidadesOrganizacionais && form.metadata.unidadesOrganizacionais.length > 0 ? (
                          <UnidadesOrgDisplay unidades={form.metadata.unidadesOrganizacionais} />
                        ) : form.metadata.unidadeOrganizacional ? (
                          <UnidadesOrgDisplay unidades={[form.metadata.unidadeOrganizacional]} />
                        ) : null}
                      </View>
                    )}
                    
                    {/* Estatísticas em cards */}
                    <View style={styles.formCardStats}>
                      <Text style={styles.formStat}>
                        📝 {form.totalQuestions} requisito{form.totalQuestions !== 1 ? 's' : ''}
                      </Text>
                      <Text style={styles.formStat}>
                        ⏱️ ~{form.estimatedTime} min
                      </Text>
                      {essentialCount > 0 && (
                        <Text style={[styles.formStat, styles.formEssentialStat]}>
                          ⚠️ {essentialCount} RE
                        </Text>
                      )}
                      <Text style={[
                        styles.formStat, 
                        styles.formComplexityStat, 
                        {color: complexity.color}
                      ]}>
                        {complexity.icon} {complexity.label}
                      </Text>
                    </View>
                    
                    <Divider style={styles.divider} />
                    
                    <View style={styles.formCardActions}>
                      <Button 
                        mode="outlined"
                        onPress={() => loadFormForEdit(form)}
                        icon="pencil"
                        compact
                        style={styles.editButton}
                        textColor={AVAQualisTheme.colors.primary}
                      >
                        ✏️ Editar
                      </Button>
                      
                      {/* Mostrar o botão Upload apenas se o formulário NÃO estiver no servidor */}
                      {!existsOnServer && (
                        <Button 
                          mode="contained"
                          onPress={() => uploadFormToCloud(form)}
                          loading={uploading}
                          icon="cloud-upload"
                          compact
                          style={styles.uploadButton}
                          buttonColor={AVAQualisTheme.colors.info}
                        >
                          ☁️ Upload
                        </Button>
                      )}
                      
                      <Button 
                        mode="outlined"
                        onPress={() => deleteCustomForm(form.id, form.title)}
                        icon="delete"
                        compact
                        style={styles.deleteButton}
                        textColor={AVAQualisTheme.colors.error}
                      >
                        🗑️ Excluir
                      </Button>
                    </View>
                  </Card.Content>
                </Card>
              )})
            )}
          </View>
        )}
        
        <View style={styles.bottomSpacer} />
      </ScrollView>

      {/* Modal de Seleção de Avaliadores */}
      <Modal
        visible={avaliadorModalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setAvaliadorModalVisible(false)}
      >
        <TouchableOpacity 
          style={styles.modalOverlay} 
          activeOpacity={1} 
          onPress={() => setAvaliadorModalVisible(false)}
        >
          <TouchableOpacity 
            style={styles.avaliadorModalContainer} 
            activeOpacity={1}
            onPress={() => {}}
          >
            <View style={styles.avaliadorModalHeader}>
              <Text style={styles.avaliadorModalTitle}>
                Selecionar {avaliadorModalType}º Avaliador
              </Text>
              <TouchableOpacity 
                onPress={() => setAvaliadorModalVisible(false)}
                style={styles.closeModalButton}
              >
                <Text style={styles.closeModalText}>✕</Text>
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.avaliadorModalContent}>
              <Text style={styles.avaliadorModalSubtitle}>
                📋 Escolha o {avaliadorModalType === 1 ? 'primeiro' : 'segundo'} avaliador da lista:
              </Text>
              
              {getAvailableAvaliadores().map((avaliador) => (
                <TouchableOpacity
                  key={avaliador.id}
                  style={styles.avaliadorModalItem}
                  onPress={() => selectAvaliadorFromModal(avaliador.id)}
                >
                  <View style={styles.avaliadorModalItemContent}>
                    <Text style={styles.avaliadorModalItemName}>
                      👤 {avaliador.nome}
                    </Text>
                    <Text style={styles.avaliadorModalItemLogin}>
                      📧 {avaliador.login}
                    </Text>
                  </View>
                  <Text style={styles.avaliadorModalItemArrow}>➤</Text>
                </TouchableOpacity>
              ))}
              
              {getAvailableAvaliadores().length === 0 && (
                <View style={styles.noAvaliadorContainer}>
                  <Text style={styles.noAvaliadorText}>
                    ⚠️ Nenhum avaliador disponível
                  </Text>
                  <Text style={styles.noAvaliadorSubtext}>
                    Remova a seleção do outro avaliador primeiro
                  </Text>
                </View>
              )}
            </ScrollView>
            
            <View style={styles.avaliadorModalFooter}>
              <TouchableOpacity
                style={styles.cancelAvaliadorButton}
                onPress={() => setAvaliadorModalVisible(false)}
              >
                <Text style={styles.cancelAvaliadorText}>Cancelar</Text>
              </TouchableOpacity>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Loading Overlay */}
      {(saving || uploading) && (
        <View style={styles.loadingOverlay}>
          <View style={styles.loadingContent}>
            <ActivityIndicator size="large" color={AVAQualisTheme.colors.primary} />
            <Text style={styles.loadingText}>
              {saving ? '💾 Salvando roteiro...' : '☁️ Enviando para nuvem...'}
            </Text>
            <Text style={styles.loadingSubtext}>
              ⏳ Aguarde um momento
            </Text>
          </View>
        </View>
      )}

      {/* Dialog Personalizado */}
      <CustomDialog
        visible={dialogVisible}
        onDismiss={() => setDialogVisible(false)}
        title={dialogContent.title}
        message={dialogContent.message}
        actions={dialogContent.actions}
      />
    </View>
  );
};